import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Trophy, Award, ArrowUpRight, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import AmbientBackground from '../common/AmbientBackground';

// Bold editorial statement with liquid glass accent card
const StatementBlock = () => {
  const { isDarkMode } = useTheme();

  return (
    <section className="py-16 sm:py-20 lg:py-28 relative overflow-hidden">
      <AmbientBackground variant="green" />

      <div className="container-custom relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Big statement - centered on mobile, left on desktop */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Pill badge */}
              <div className="inline-flex items-center justify-center gap-2 mb-6 sm:mb-8">
                <div className={`h-px w-8 ${
                  isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'
                }`} />
                <span className={`text-[10px] sm:text-xs font-semibold uppercase tracking-[0.3em] ${
                  isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                }`}>
                  Est. 1951
                </span>
                <div className={`h-px w-8 ${
                  isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'
                }`} />
              </div>

              <h2 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[8rem] font-bold leading-[0.9] font-cursive mb-6 sm:mb-8 tracking-tight ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}>
                75 years.
                <br />
                <span className={`bg-clip-text text-transparent ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500'
                    : 'bg-gradient-to-r from-emerald-600 via-emerald-700 to-emerald-800'
                }`}>
                  45 clubs.
                </span>
                <br />
                One union.
              </h2>

              <p className={`text-base sm:text-lg lg:text-xl leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8 sm:mb-10 ${
                isDarkMode ? 'text-slate-400' : 'text-slate-600'
              }`}>
                The governing body for amateur golf in the Free State — championing
                excellence, integrity, and the spirit of the game across every
                fairway in the province.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link
                  to="/tournaments"
                  className="group relative inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                    boxShadow: '0 10px 40px -10px rgba(16, 185, 129, 0.6)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <Trophy className="w-4 h-4 relative" />
                  <span className="relative">View Tournaments</span>
                  <ArrowUpRight className="w-4 h-4 relative group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
                <Link
                  to="/results"
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-all duration-300 hover:scale-[1.02] backdrop-blur-xl ${
                    isDarkMode
                      ? 'bg-white/[0.05] border border-white/10 text-white hover:bg-white/[0.08]'
                      : 'bg-white/60 border border-slate-200 text-slate-900 hover:bg-white/80'
                  }`}
                >
                  <Award className="w-4 h-4" />
                  Latest Results
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Right: Liquid glass numbers card */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto max-w-md lg:max-w-none"
            >
              {/* Outer glow */}
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-emerald-500/30 via-emerald-600/20 to-amber-500/20 blur-2xl opacity-60" />

              {/* Glass card */}
              <div className={`relative rounded-3xl overflow-hidden backdrop-blur-2xl border p-7 sm:p-9 ${
                isDarkMode
                  ? 'bg-gradient-to-br from-white/[0.08] via-white/[0.03] to-white/[0.05] border-white/[0.12]'
                  : 'bg-gradient-to-br from-white/80 via-white/60 to-white/70 border-white/90'
              }`}>
                {/* Top shine */}
                <div
                  className="absolute inset-x-0 top-0 h-px"
                  style={{
                    background: isDarkMode
                      ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)'
                      : 'linear-gradient(90deg, transparent, rgba(255,255,255,1), transparent)'
                  }}
                />

                {/* Decorative circles */}
                <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-emerald-500/10" />
                <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full bg-amber-500/10" />

                <div className="relative z-10 space-y-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className={`text-[10px] sm:text-xs font-semibold uppercase tracking-widest mb-2 ${
                        isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                      }`}>
                        Established
                      </div>
                      <div className={`text-6xl sm:text-7xl font-bold font-cursive leading-none ${
                        isDarkMode ? 'text-white' : 'text-slate-900'
                      }`}>
                        1951
                      </div>
                    </div>
                    <Sparkles className={`w-6 h-6 ${
                      isDarkMode ? 'text-amber-400' : 'text-amber-500'
                    }`} />
                  </div>

                  <div className={`h-px ${
                    isDarkMode ? 'bg-white/10' : 'bg-slate-900/10'
                  }`} />

                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <div className={`text-3xl sm:text-4xl font-bold mb-1 ${
                        isDarkMode ? 'text-white' : 'text-slate-900'
                      }`}>
                        5,500
                        <span className={isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}>+</span>
                      </div>
                      <div className={`text-xs sm:text-sm ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-600'
                      }`}>
                        Registered Players
                      </div>
                    </div>
                    <div>
                      <div className={`text-3xl sm:text-4xl font-bold mb-1 ${
                        isDarkMode ? 'text-white' : 'text-slate-900'
                      }`}>
                        12
                      </div>
                      <div className={`text-xs sm:text-sm ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-600'
                      }`}>
                        Major Events/Year
                      </div>
                    </div>
                  </div>

                  <div className={`h-px ${
                    isDarkMode ? 'bg-white/10' : 'bg-slate-900/10'
                  }`} />

                  <blockquote className={`text-sm sm:text-base italic leading-relaxed ${
                    isDarkMode ? 'text-slate-300' : 'text-slate-700'
                  }`}>
                    "Promoting the game of golf in the Free State with passion,
                    integrity, and excellence."
                  </blockquote>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatementBlock;